// ══════════════════════════════════════════════════════════════════════════
//  Calendario laboral — motor puro (sin React, sin DB)
//
//  UNA sola verdad de "cuándo se trabaja", en cascada:
//    contrato (presupuesto.config_planeacion.jornada)
//      → obra (obras.calendario_laboral)
//        → general (admin_config.calendario_laboral)
//  El primer nivel con jornada ACTIVA manda. Si ninguno existe, el motor
//  devuelve null y quien lo consume degenera a la aritmética natural (días
//  corridos): la ausencia de calendario ES el comportamiento clásico, así
//  nada cambia donde nadie lo configuró.
//
//  Forma de la jornada (la misma que ya usa Planeación en su programa de obra):
//    { activa, dias: [dow...] (0 dom … 6 sáb), hora_inicio: 'HH:MM', hora_fin: 'HH:MM',
//      feriados_ley: true, feriados_extra: [iso], feriados_excluidos: [iso] }
//  `feriados_ley` mete los descansos obligatorios de la LFT art. 74 de cada año;
//  `feriados_excluidos` marca "aquí sí se trabaja ese día".
//
//  Consumidores hoy: la Mesa de estimaciones (huecos sin estimar). Planeación
//  sigue con su propia compilación (mismo modelo, misma tabla de feriados).
// ══════════════════════════════════════════════════════════════════════════

export const JORNADA_DEFAULT = Object.freeze({
  activa: true,
  dias: [1, 2, 3, 4, 5, 6],
  hora_inicio: '08:00',
  hora_fin: '18:00',
  feriados_ley: true,
  feriados_extra: [],
  feriados_excluidos: [],
});

// Jornada "días corridos" EXPLÍCITA: todos los días cuentan y no hay feriados.
// Es como una obra se sale del calendario general (null = heredarlo).
export function jornadaCorrida(base = JORNADA_DEFAULT) {
  return {
    activa: true,
    dias: [0, 1, 2, 3, 4, 5, 6],
    hora_inicio: base?.hora_inicio || JORNADA_DEFAULT.hora_inicio,
    hora_fin: base?.hora_fin || JORNADA_DEFAULT.hora_fin,
    feriados_ley: false,
    feriados_extra: [],
    feriados_excluidos: [],
  };
}

export const dow = (iso) => { const [y, m, d] = String(iso).split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)).getUTCDay(); };
export const addDias = (iso, n) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
};

// Descansos obligatorios LFT art. 74 del año dado (única tabla del proyecto).
export function feriadosLeyMX(anio) {
  const nthLunes = (mes, n) => {
    let d = `${anio}-${String(mes).padStart(2, '0')}-01`, c = 0;
    for (let i = 0; i < 31; i++) {
      if (dow(d) === 1 && ++c === n) return d;
      d = addDias(d, 1);
    }
    return d;
  };
  const out = [
    { iso: `${anio}-01-01`, nombre: 'Año Nuevo' },
    { iso: nthLunes(2, 1),  nombre: 'Día de la Constitución' },
    { iso: nthLunes(3, 3),  nombre: 'Natalicio de Benito Juárez' },
    { iso: `${anio}-05-01`, nombre: 'Día del Trabajo' },
    { iso: `${anio}-09-16`, nombre: 'Independencia' },
    { iso: nthLunes(11, 3), nombre: 'Revolución Mexicana' },
    { iso: `${anio}-12-25`, nombre: 'Navidad' },
  ];
  if (anio >= 2024 && (anio - 2024) % 6 === 0) out.push({ iso: `${anio}-10-01`, nombre: 'Transmisión del Ejecutivo Federal' });
  return out.sort((a, z) => (a.iso < z.iso ? -1 : 1));
}

const DIA_VALIDO = (d) => Number.isInteger(d) && d >= 0 && d <= 6;

// Acepta objeto o JSON (admin_config guarda texto). Devuelve la jornada
// normalizada o null si no existe / está inactiva / es inválida.
export function parsearJornada(raw) {
  if (raw == null || raw === '') return null;
  let j = raw;
  if (typeof raw === 'string') { try { j = JSON.parse(raw); } catch (e) { return null; } }
  if (!j || typeof j !== 'object' || j.activa === false) return null;
  const dias = [...new Set((Array.isArray(j.dias) ? j.dias : JORNADA_DEFAULT.dias).map(Number).filter(DIA_VALIDO))].sort((a, b) => a - b);
  if (!dias.length) return null;
  return {
    activa: true,
    dias,
    hora_inicio: typeof j.hora_inicio === 'string' ? j.hora_inicio : JORNADA_DEFAULT.hora_inicio,
    hora_fin: typeof j.hora_fin === 'string' ? j.hora_fin : JORNADA_DEFAULT.hora_fin,
    feriados_ley: j.feriados_ley !== false,
    feriados_extra: (Array.isArray(j.feriados_extra) ? j.feriados_extra : []).filter(Boolean),
    feriados_excluidos: (Array.isArray(j.feriados_excluidos) ? j.feriados_excluidos : []).filter(Boolean),
    // Horario especial por día (ej. sábado corto): parte de la jornada, no un
    // detalle; solo de días laborables y con ambas horas.
    horarios_dia: normalizarHorariosDia(j.horarios_dia, dias),
  };
}

function normalizarHorariosDia(raw, dias) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [k, h] of Object.entries(raw)) {
    const d = Number(k);
    if (!DIA_VALIDO(d) || !dias.includes(d) || !h || typeof h.hora_inicio !== 'string' || typeof h.hora_fin !== 'string') continue;
    out[String(d)] = { hora_inicio: h.hora_inicio, hora_fin: h.hora_fin };
  }
  return out;
}

export function esJornadaCorrida(raw) {
  const j = parsearJornada(raw);
  return !!j && j.dias.length === 7 && j.feriados_ley === false && j.feriados_extra.length === 0;
}

// Cómo está configurado un nivel (la obra): 'hereda' (sin jornada propia o
// inactiva), 'corrido' (todos los días, sin feriados) o 'propio'.
export function modoJornada(raw) {
  const j = parsearJornada(raw);
  if (!j) return 'hereda';
  return esJornadaCorrida(j) ? 'corrido' : 'propio';
}

// Cascada: contrato → obra → general. Devuelve { jornada, fuente } o null.
export function resolverJornada({ general, obra, contrato } = {}) {
  const niveles = [
    ['contrato', contrato?.config_planeacion?.jornada],
    ['obra',     obra?.calendario_laboral],
    ['general',  general],
  ];
  for (const [fuente, raw] of niveles) {
    const jornada = parsearJornada(raw);
    if (jornada) return { jornada, fuente };
  }
  return null;
}

// Compila la jornada a un objeto de consulta. Ventana fija de años para los
// feriados de ley (los períodos de estimación son de corto plazo).
export function compilarCalendario(jornada, fuente = null, anioBase = new Date().getUTCFullYear()) {
  if (!jornada) return null;
  const dias = new Set(jornada.dias);
  const excl = new Set(jornada.feriados_excluidos || []);
  const feriados = new Set();
  if (jornada.feriados_ley !== false) {
    for (let y = anioBase - 1; y <= anioBase + 7; y++) {
      for (const f of feriadosLeyMX(y)) if (!excl.has(f.iso)) feriados.add(f.iso);
    }
  }
  for (const f of (jornada.feriados_extra || [])) if (f && !excl.has(f)) feriados.add(f);
  const excluidos = [0, 1, 2, 3, 4, 5, 6].filter(d => !dias.has(d));
  // `jornada` viaja completa para que la UI la describa con horarios especiales.
  return { dias, feriados, excluidos, hora_inicio: jornada.hora_inicio, hora_fin: jornada.hora_fin, horarios_dia: jornada.horarios_dia || {}, fuente, jornada };
}

export function calendarioDe(niveles, anioBase) {
  const r = resolverJornada(niveles);
  return r ? compilarCalendario(r.jornada, r.fuente, anioBase) : null;
}

// cal null → todo día es laborable (aritmética natural).
export const esLaborable = (iso, cal) => !cal || (cal.dias.has(dow(iso)) && !cal.feriados.has(iso));

// Días laborables en [a, b] inclusive, en orden.
export function laborablesEntre(a, b, cal) {
  const out = [];
  if (!a || !b || b < a) return out;
  for (let d = a, i = 0; d <= b && i < 40000; d = addDias(d, 1), i++) if (esLaborable(d, cal)) out.push(d);
  return out;
}

export const siguienteLaborable = (iso, cal) => {
  let d = iso;
  for (let i = 0; i < 400 && !esLaborable(d, cal); i++) d = addDias(d, 1);
  return d;
};

// Etiqueta de la jornada para la UI. Los horarios especiales son parte de la
// jornada: "lunes a viernes, 08:00 a 17:00; sábado, 08:00 a 13:00". Decir
// "lunes a sábado, 08:00 a 17:00" con sábado corto sería mentir.
const NOMBRES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const ORDEN_LUNES = (d) => (d + 6) % 7;   // la semana se lee de lunes a domingo

// "lunes a viernes", "lunes a viernes y domingo", "lunes, miércoles".
function nombrarDias(dias) {
  const ord = [...dias].sort((a, b) => ORDEN_LUNES(a) - ORDEN_LUNES(b));
  const corridas = [];
  for (const d of ord) {
    const ult = corridas[corridas.length - 1];
    if (ult && ORDEN_LUNES(d) === ORDEN_LUNES(ult[ult.length - 1]) + 1) ult.push(d);
    else corridas.push([d]);
  }
  const partes = corridas.map(r => r.length >= 3 ? `${NOMBRES[r[0]]} a ${NOMBRES[r[r.length - 1]]}` : r.map(x => NOMBRES[x]).join(', '));
  return partes.length > 1 ? `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}` : partes[0];
}

export function describirJornada(jornada) {
  if (!jornada) return 'días corridos';
  const dias = [...new Set((jornada.dias || []).map(Number).filter(DIA_VALIDO))];
  if (!dias.length) return 'días corridos';
  const base = `${jornada.hora_inicio} a ${jornada.hora_fin}`;
  const hd = jornada.horarios_dia || {};
  // Días agrupados por horario efectivo: el base primero, luego cada especial
  // en el orden de la semana. Un especial igual al base se funde con él.
  const grupos = new Map([[base, []]]);
  for (const d of [...dias].sort((a, b) => ORDEN_LUNES(a) - ORDEN_LUNES(b))) {
    const h = hd[String(d)];
    const clave = (h && h.hora_inicio && h.hora_fin) ? `${h.hora_inicio} a ${h.hora_fin}` : base;
    if (!grupos.has(clave)) grupos.set(clave, []);
    grupos.get(clave).push(d);
  }
  const partes = [];
  for (const [horario, ds] of grupos) {
    if (!ds.length) continue;
    partes.push(`${ds.length === 7 ? 'todos los días' : nombrarDias(ds)}, ${horario}`);
  }
  return partes.join('; ');
}
